import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Inbox, MailOpen } from "lucide-react";
import { z } from "zod";

import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { fetchNotifications } from "@/lib/queries";
import { listMyNotices } from "@/lib/account.functions";
import { parseInternalLink } from "@/lib/internal-link";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  m: z.string().optional(),
  n: z.string().optional(),
  filter: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/messages")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Messages — Quibblies" },
      {
        name: "description",
        content:
          "Your Quibblies inbox: approval decisions, moderation updates, tips and community activity, opened in place.",
      },
      { property: "og:title", content: "Messages — Quibblies" },
      { property: "og:description", content: "Read your Quibblies approval and account messages." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MessagesPage,
});

function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate({ from: "/messages" });
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const loadNotices = useServerFn(listMyNotices);

  const notifications = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => fetchNotifications(user!.id),
    enabled: Boolean(user),
  });
  const notices = useQuery({
    queryKey: ["my-notices"],
    queryFn: () => loadNotices(),
    retry: false,
  });

  const rows = notifications.data ?? [];
  const unread = rows.filter((n) => !n.read_at).length;

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", id)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", user!.id)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  const filter = search.filter ?? "all";
  const term = (search.q ?? "").trim().toLowerCase();
  const matches = (...parts: Array<string | null | undefined>) =>
    !term || parts.filter(Boolean).join(" ").toLowerCase().includes(term);

  const visible = rows.filter(
    (n) =>
      (filter === "unread" ? !n.read_at : true) && matches(n.title, n.body, n.category),
  );
  const visibleNotices = (notices.data ?? []).filter((n) => matches(n.subject, n.body));

  const selected = rows.find((n) => n.id === search.m) ?? null;
  const selectedNotice = (notices.data ?? []).find((n) => n.id === search.n) ?? null;

  // Opening a message marks it read; no navigation away from the inbox.
  React.useEffect(() => {
    if (selected && !selected.read_at) markRead.mutate(selected.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  function open(next: { m?: string; n?: string }) {
    void navigate({ search: (prev) => ({ ...prev, m: undefined, n: undefined, ...next }) });
  }

  const target = parseInternalLink(selected?.link ?? null);

  // Everything sharing a link belongs to the same decision, so it reads as one
  // conversation with its earlier steps kept in view.
  const threadKey = selected ? (selected.link ?? `${selected.category}:${selected.title}`) : null;
  const thread = selected
    ? rows
        .filter((n) => (n.link ?? `${n.category}:${n.title}`) === threadKey)
        .slice()
        .sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        )
    : [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl text-foreground">Messages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Approval decisions, moderation updates and community activity — read here without
            being sent anywhere else.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 ? (
            <Button variant="soft" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
              Mark all as read
            </Button>
          ) : null}
          <Button asChild variant="ghost">
            <Link to="/settings/notifications">Email settings</Link>
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filter messages">
        {[
          { id: "all", label: `All (${rows.length})` },
          { id: "unread", label: `Unread (${unread})` },
        ].map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={filter === option.id}
            onClick={() => void navigate({ search: (prev) => ({ ...prev, filter: option.id }) })}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm transition-smooth",
              filter === option.id
                ? "border-primary/60 bg-primary/10 text-foreground"
                : "border-border/70 text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <input
          type="search"
          value={search.q ?? ""}
          onChange={(event) =>
            void navigate({
              search: (prev) => ({ ...prev, q: event.target.value || undefined }),
            })
          }
          placeholder="Search by sender, subject or keyword"
          aria-label="Search messages"
          className="w-full rounded-full border border-border/70 bg-surface/40 px-4 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary/60"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <div className="space-y-2">
          {notifications.isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-16 rounded-2xl" />
            ))
          ) : visible.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No messages yet"
              description="Approvals, moderation updates and tips land here."
            />
          ) : (
            visible.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => open({ m: notification.id })}
                className={cn(
                  "w-full rounded-2xl border border-border/70 bg-gradient-card p-4 text-left transition-smooth hover:border-primary/50",
                  !notification.read_at && "border-primary/50",
                  search.m === notification.id && "bg-accent",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[0.7rem] uppercase tracking-wide text-primary-glow">
                    {notification.category}
                  </span>
                  {!notification.read_at ? (
                    <span className="h-2 w-2 rounded-full bg-primary" aria-label="Unread" />
                  ) : null}
                </div>
                <p className="mt-1 line-clamp-2 text-sm font-medium text-foreground">
                  {notification.title}
                </p>
                <p className="mt-1 text-[0.7rem] text-muted-foreground">
                  {new Date(notification.created_at).toLocaleString()}
                </p>
              </button>
            ))
          )}

          {visibleNotices.length ? (
            <div className="pt-4">
              <h2 className="px-1 text-xs uppercase tracking-wide text-muted-foreground">
                Decision letters
              </h2>
              <div className="mt-2 space-y-2">
                {visibleNotices.map((notice) => (
                  <button
                    key={notice.id}
                    type="button"
                    onClick={() => open({ n: notice.id })}
                    className={cn(
                      "w-full rounded-2xl border border-border/70 bg-surface/40 p-4 text-left transition-smooth hover:border-primary/50",
                      search.n === notice.id && "bg-accent",
                    )}
                  >
                    <p className="line-clamp-2 text-sm font-medium text-foreground">
                      {notice.subject}
                    </p>
                    <p className="mt-1 text-[0.7rem] text-muted-foreground">
                      {new Date(notice.created_at).toLocaleString()} · {notice.delivery_status}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <section className="rounded-3xl border border-border/70 bg-surface/40 p-6">
          {selected ? (
            <>
              <p className="text-xs uppercase tracking-wide text-primary-glow">
                {selected.category}
              </p>
              <h2 className="mt-2 font-display text-2xl text-foreground">{selected.title}</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {new Date(selected.created_at).toLocaleString()}
              </p>
              {selected.body ? (
                <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground">
                  {selected.body}
                </p>
              ) : null}
              {thread.length > 1 ? (
                <div className="mt-6 border-t border-border/60 pt-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Conversation history
                  </p>
                  <ol className="mt-3 space-y-3 border-l border-border/60 pl-3">
                    {thread.map((entry) => (
                      <li key={entry.id}>
                        <p className="text-[0.7rem] text-muted-foreground">
                          {new Date(entry.created_at).toLocaleString()}
                        </p>
                        <p
                          className={cn(
                            "text-sm",
                            entry.id === selected.id
                              ? "font-medium text-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          {entry.title}
                        </p>
                        {entry.body ? (
                          <p className="whitespace-pre-line text-sm text-muted-foreground">
                            {entry.body}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
              {target ? (
                <Button
                  className="mt-6"
                  variant="hero"
                  size="sm"
                  onClick={() =>
                    void navigate({
                      to: target.to,
                      ...(target.search ? { search: target.search } : {}),
                    } as never)
                  }
                >
                  Open the linked page
                </Button>
              ) : null}
            </>
          ) : selectedNotice ? (
            <>
              <p className="text-xs uppercase tracking-wide text-primary-glow">Decision letter</p>
              <h2 className="mt-2 font-display text-2xl text-foreground">
                {selectedNotice.subject}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {new Date(selectedNotice.created_at).toLocaleString()} ·{" "}
                {selectedNotice.delivery_status}
              </p>
              <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground">
                {selectedNotice.body}
              </p>
              <Button asChild className="mt-6" variant="soft" size="sm">
                <Link to="/account/status" search={{ section: "messages" }}>
                  Go to account status
                </Link>
              </Button>
            </>
          ) : (
            <EmptyState
              icon={MailOpen}
              title="Pick a message"
              description="Choose anything on the left to read it here."
            />
          )}
        </section>
      </div>
    </div>
  );
}
